/**
 * ResposiveBar
 * 
 * @Author: Focci
 * @Date: 2023-10-05 15:00:53
 * @Last Modified by: Focci
 * @Last Modified time: 2023-10-05 15:00:53
 */

'use client'

import PropTypes from 'prop-types'
import useApp from '@hook/useApp'
import AgentEnquiry from '@comp/dialog/agent-enquiry'
import useTracking from '@hook/useTracking'
import Image from 'next/image'
import clsx from 'clsx'
import { Button, ButtonBase } from '@comp/material'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import { isArray } from 'lodash-es'
import { getEnquiryProps } from '../agent-card/lib'

function ResposiveBarScoff({ 
  children,
  className = 'px-2 pt-2 pb-6 bg-white',
  ...props
}) {
  return (
    <div 
      className={clsx(['fixed left-0 right-0 bottom-0 border-t z-50', className])}
      {...props}
    >
      {children}
    </div>
  )
}

function NormalButton({
  isPublisher,
  ...props
}) {
  const th = useTranslations('house')

  return (
    <Button sx={{ p: 0 }} className="w-full" {...props}>
      <div className="h-10 text-f.8 flex items-center justify-center w-full">
        {isPublisher ? th('contactTa') : th('contactAgent')}
      </div>
    </Button>
  )
}

function PromotionAgentBar({ 
  agents,
  onClick = () => {}
}) {
  const th = useTranslations('house')

  if(!(isArray(agents) && agents.length > 0)) {
    return null
  }
  const agent = agents[0]

  return (
    <ResposiveBarScoff 
      className="px-2 pt-2 pb-6"
      style={{ backgroundColor: agent.bg_color, color: agent.font_color }}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-1 shrink-0 -mt-4 mr-2">
          {agents.map((ag, index) => ag.oss_avatar && (
            <Image
              key={ag.oss_avatar}
              priority
              src={ag.oss_avatar || ''}
              width={70}
              height={70}
              alt={ag.name}
              className={clsx([
                'object-cover object-top rounded-full w-14 h-14', 
                index > 0 ? '-ml-4 z-0' : 'z-1'
              ])}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {agent.office_oss_avatar && (
            <Image
              priority
              src={agent.office_oss_avatar || ''}
              width={120}
              height={30}
              alt={agent.office_name}
              className="object-contain max-h-[2rem]"
            />
          )}
          <ButtonBase onClick={onClick}>
            <div
              className="h-9 flex items-center px-3 rounded-[.2rem] text-f.8 whitespace-nowrap" 
              style={{ 
                color: agent.font_color,
                border: `solid 1px ${agent.font_color}`
              }}
            >
              {th('contactAgent')}
            </div>
          </ButtonBase>
        </div>
      </div>
    </ResposiveBarScoff>
  )
}

export default function ResposiveBar({
  isPromotion = true,
  isPublisher = false,
  agents = [],
  house = {}
}) {
  const { isMobile } = useApp()
  const [open, setOpen] = useState(false)
  const { pushTrackListing, pushTrack } = useTracking()

  const enquiryProps = useMemo(() => getEnquiryProps(house), [house])

  const handleClick = useCallback(() => {
    // 添加统计事件
    pushTrackListing('click_email', house)
    pushTrack('click_email', {
      houseId: house.houseId || 0,
      agentId: agents?.[0]?.id,
      agencyOfficeId: agents?.[0]?.office_id
    })

    setOpen(true)
  }, [agents, house, pushTrack, pushTrackListing])
  
  if(!isMobile) {
    return null
  }

  return (
    <>
      {
        isPromotion && !isPublisher
          ? <PromotionAgentBar agents={agents} onClick={handleClick} />
          : (
            <ResposiveBarScoff>
              <NormalButton 
                isPublisher={isPublisher} 
                onClick={handleClick} 
              />
            </ResposiveBarScoff>
          )
      }
      <AgentEnquiry
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={() => setOpen(false)}
        {...enquiryProps}
      />
    </>
  )
}

ResposiveBar.propTypes = {
  agents: PropTypes.array,
}
